import { useState } from 'react';

import Popover from '@mui/material/Popover';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import {
    GridColumnMenu,
} from '@mui/x-data-grid';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { IndigoSvg } from 'BMapsSrc/IndigoSvg';
import BMapsTooltip from '../BMapsTooltip';

/**
 * @typedef {import('BMapsModel').Compound} Compound
 * @typedef {import('BMapsModel').MapCase} MapCase
 * @typedef {import('@mui/x-data-grid').GridValidRowModel} GridValidRowModel
 */

/**
 * @param {{
 *    smiles: Compound['smiles'],
 *    row: GridValidRowModel,
 * }} props
 * @returns
 */
export function HoverableCompoundStructureCell({ smiles, row }) {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleOpen = (event) => {
        setAnchorEl(event.currentTarget);
        setTimer(setTimeout(handleClose, 2000));
    };

    const handleClose = () => {
        clearTimeout(timer);
        setAnchorEl(null);
    };

    const [timer, setTimer] = useState(null);

    const open = Boolean(anchorEl);
    const id = open ? `${smiles}-${row.id}-popoup` : undefined;

    return (
        <div
            style={{
                height: '100%',
                width: '100%',
            }}
        >
            <div
                aria-describedby={id}
                onFocus={handleOpen}
                onMouseOver={handleOpen}
                onBlur={handleClose}
                onMouseLeave={handleClose}
                style={{
                    height: '100%',
                    width: '100%',
                }}
            >
                <IndigoSvg
                    smiles={smiles}
                    style={{
                        padding: '0',
                        height: '100%',
                        width: '100%',
                    }}
                    autoUpdate
                    alt={`2D structure of ${row['Compound Name']}`}
                />
            </div>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                style={{ pointerEvents: 'none' }}
            >
                <div style={{ position: 'relative', overflow: 'hidden' }}>
                    <IndigoSvg
                        smiles={smiles}
                        autoUpdate
                        style={{
                            padding: '0',
                            height: '6rem',
                            overflow: 'hidden',
                        }}
                        alt=""
                    />
                </div>
            </Popover>
        </div>
    );
}

/**
 * @param {{
 *    value: number,
 *    mapCase: MapCase,
 *    row: GridValidRowModel,
 *    index: number
 *    useUniqueCompounds?: boolean
 * }} props
 * @returns
 */
export function ProteinCell({
    value, mapCase, row, index, useUniqueCompounds=false,
}) {
    let tooltip = getFormattedValue(value);

    if (useUniqueCompounds) {
        const bestPoseKey = `Best Pose Name ${mapCase.displayName} (Protein ${index + 1})`;
        const bestPoseName = row[bestPoseKey];
        // Option to activate the best pose
        // const { caseData } = App.getDataParents(mapCase);
        // const bestPose = caseData.getCompoundBySpec(bestPoseName);
        // const bestPoseActivate = (
        //     <LinkLikeButton
        //         onClick={() => UserActions.ActivateCompound(bestPose)}
        //         title={`Focus on ${bestPoseName}`}
        //     >
        //         {bestPoseName}
        //     </LinkLikeButton>
        // );

        tooltip = (
            <>
                {getFormattedValue(value)}
                <br />
                <br />
                Top scoring pose for
                {' '}
                {mapCase.getLongName()}
                {' is '}
                {bestPoseName}
            </>
        );
    }

    return (
        <BMapsTooltip title={tooltip} placement="bottom-start">
            <span>{ getFormattedValue(value) }</span>
        </BMapsTooltip>
    );
}

export function CompoundNameCell({ row, value }) {
    const resSpec = row.resSpec;
    return (
        <BMapsTooltip title={resSpec}>
            <span>{ value }</span>
        </BMapsTooltip>
    );
}

/**
 * @param {string | number} value
 * @returns
 */
export function DefaultCell(value) {
    return getFormattedValue(value);
}

/**
 * @param {string | number} value
 * @returns
 */
function getFormattedValue(value) {
    if (typeof value !== 'number') return value;
    if (Number.isInteger(value)) return value;
    return value.toFixed(2);
}

export function HeaderCell(props) {
    const { colDef, content, tooltip } = props;
    const headerContent = content || colDef.headerName;
    const tooltipContent = tooltip || colDef.description;
    return (
        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <HeaderMenuButton {...props} />
            <BMapsTooltip title={tooltipContent} noMaxWidth>
                <div
                    style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flexGrow: 1,
                    }}
                >
                    {headerContent}
                </div>
            </BMapsTooltip>
        </div>
    );
}

export function HeaderMenuButton(props) {
    const { colDef } = props;
    const [anchorEl, setAnchorEl] = useState(null);
    const menuOpen = Boolean(anchorEl);
    const tooltipTitle = menuOpen ? undefined : colDef.description;

    const handleMenuClick = (event) => {
        event.stopPropagation(); // Don't trigger sorting
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    return (
        <div
            className="compoundTable-customMenuButton"
            style={{ marginLeft: 'auto' }}
            aria-expanded={menuOpen ? 'true' : undefined}
        >
            <BMapsTooltip
                title={tooltipTitle}
                noMaxWidth
                enterNextDelay={200}
            >
                <IconButton
                    aria-label={`Actions for ${colDef.headerName}`}
                    onClick={handleMenuClick}
                    size="small"
                    sx={{ padding: 0.5 }}
                >
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            </BMapsTooltip>
            <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleMenuClose}>
                <GridColumnMenu
                    {...props}
                    open={menuOpen}
                    hideMenu={handleMenuClose}
                />
            </Menu>
        </div>
    );
}
